import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import pushManager from '../utils/pushNotifications.js';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
    const ctx = useContext(SubscriptionContext);
    if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
    return ctx;
};

let globalSubscriptionState = {
    isAttempting: false,
    lastAttempt: 0,
    attempts: 0,
};

let globalStateTimeout = null;

function setGlobalSubscriptionAttempting(isAttempting) {
    globalSubscriptionState.isAttempting = isAttempting;
    if (isAttempting) {
        globalSubscriptionState.lastAttempt = Date.now();
        globalSubscriptionState.attempts++;

        if (globalStateTimeout) clearTimeout(globalStateTimeout);
        globalStateTimeout = setTimeout(() => {
            globalSubscriptionState = { isAttempting: false, lastAttempt: 0, attempts: 0 };
            globalStateTimeout = null;
            // eslint-disable-next-line no-console
            console.warn('[SubscriptionContext] Global subscription state timeout, resetting...');
        }, 30000);
    } else {
        if (globalStateTimeout) {
            clearTimeout(globalStateTimeout);
            globalStateTimeout = null;
        }
    }
}

export function SubscriptionProvider({ children }) {
    const [isSupported, setIsSupported] = useState(false);
    const [permission, setPermission] = useState('default');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const updateState = useCallback(async () => {
        try {
            const capabilities = pushManager.getCapabilities();
            const subscriptionStatus = pushManager.getSubscriptionStatus();
            setIsSupported(capabilities.supported);
            setPermission(capabilities.permission);
            setIsSubscribed(subscriptionStatus.isSubscribed);
            setSubscription(pushManager.subscription);
            setIsInitialized(subscriptionStatus.isInitialized);
        } catch (e) {
            setError(e?.message || 'Failed to update subscription state');
        }
    }, []);

    useEffect(() => {
        updateState();
        const interval = setInterval(updateState, 10000);
        return () => clearInterval(interval);
    }, [updateState]);

    const requestPermission = useCallback(async () => {
        if (!isSupported) throw new Error('Push notifications are not supported in this browser');
        setIsLoading(true);
        setError(null);
        try {
            const result = await pushManager.requestPermission();
            setPermission(result);
            return result;
        } catch (e) {
            setError(e?.message || 'Failed to request permission');
            throw e;
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isSupported]);

    const subscribe = useCallback(async () => {
        if (!isSupported) throw new Error('Push notifications are not supported in this browser');
        if (permission !== 'granted') throw new Error('Notification permission not granted');
        if (!isInitialized) throw new Error('Push notification system not initialized');

        setIsLoading(true);
        setError(null);
        try {
            const sub = await pushManager.subscribe();
            setSubscription(sub);
            setIsSubscribed(true);
            return sub;
        } catch (e) {
            setError(e?.message || 'Failed to subscribe');
            throw e;
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isSupported, permission, isInitialized]);

    const unsubscribe = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const success = await pushManager.unsubscribe();
            if (success) {
                setSubscription(null);
                setIsSubscribed(false);
            }
            return success;
        } catch (e) {
            setError(e?.message || 'Failed to unsubscribe');
            throw e;
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, []);

    const requestPermissionAndSubscribe = useCallback(async () => {
        const token = Cookies.get('access_token');
        if (!token) throw new Error('User not authenticated');
        const permissionResult = await requestPermission();
        if (permissionResult === 'granted') {
            await subscribe();
            return true;
        }
        return false;
    }, [requestPermission, subscribe]);

    const value = useMemo(() => ({
        // State
        isSupported,
        permission,
        isSubscribed,
        isLoading,
        error,
        subscription,
        isInitialized,

        // Actions
        requestPermission,
        subscribe,
        unsubscribe,
        requestPermissionAndSubscribe,

        // Derived
        canRequestPermission: isSupported && permission === 'default',
        canSubscribe: isSupported && permission === 'granted' && !isSubscribed && !isLoading,
        canUnsubscribe: isSupported && isSubscribed,

        // Global guard
        getGlobalState: () => ({ ...globalSubscriptionState }),
        setGlobalAttempting: setGlobalSubscriptionAttempting,
    }), [
        isSupported,
        permission,
        isSubscribed,
        isLoading,
        error,
        subscription,
        isInitialized,
        requestPermission,
        subscribe,
        unsubscribe,
        requestPermissionAndSubscribe,
    ]);

    return (
        <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
    );
}

export default SubscriptionProvider;


